import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BarChart3, CalendarDays, LayoutGrid } from 'lucide-react';
import { IS_NATIVE } from '../native/platform.js';
import {
  accessFromResponse,
  buildEcosystemApps,
  isPlainLeftClick,
  openEcosystemApp,
  visibleApps,
  windowNameFor,
} from '../lib/appSwitcher.js';

const SYNOSIGHT_URL =
  import.meta.env.VITE_SYNOSIGHT_URL || 'https://synosight.synotis.tech';

const APP_ICONS = { synolog: CalendarDays, synosight: BarChart3 };

// Onglets ouverts depuis ce menu (références perdues au rechargement : l'onglet
// est alors retrouvé par son nom, cf. openEcosystemApp).
const openedTabs = new Map();

/**
 * #23 (support) — menu « Applications » de l'en-tête (remplace le lien icône
 * vers Synosight). Chaque app s'ouvre dans son propre onglet, réutilisé au
 * clic suivant : l'onglet courant et sa session ne sont jamais quittés.
 *
 * Visibilité : un admin voit toutes les apps ; sinon une app n'est masquée
 * que si elle répond explicitement « pas d'accès » sur `/auth/access`.
 */
export default function AppSwitcherMenu({ isAdmin }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [access, setAccess] = useState({});
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Synolog = app courante : son URL n'est jamais naviguée depuis ce menu.
  const apps = useMemo(
    () =>
      buildEcosystemApps({
        synologUrl: typeof window !== 'undefined' ? window.location.origin : '',
        synosightUrl: SYNOSIGHT_URL,
      }),
    [],
  );

  useEffect(() => {
    // App native : ni cookie SSO ni vue desktop. Admin : tout est listé.
    if (IS_NATIVE || isAdmin) return undefined;
    let cancelled = false;
    for (const app of apps) {
      if (app.key === 'synolog') continue;
      fetch(app.accessUrl, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const value = accessFromResponse(data);
          if (cancelled || value === undefined) return;
          setAccess((prev) => ({ ...prev, [app.key]: value }));
        })
        .catch(() => {
          // App injoignable : on n'en conclut rien, l'entrée reste.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [apps, isAdmin]);

  const items = visibleApps(apps, { currentKey: 'synolog', isAdmin, access });

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const recompute = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    };
    recompute();
    const onScroll = () => setOpen(false);
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      const inButton = buttonRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inButton && !inDropdown) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (IS_NATIVE || items.length <= 1) return null;

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 14px',
    fontSize: 13,
    textDecoration: 'none',
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Applications"
        aria-label="Applications"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          background: open ? 'var(--glass)' : 'transparent',
          color: 'var(--text)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-sm)',
          padding: 6,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <LayoutGrid size={16} />
      </button>

      {open && anchor &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            aria-label="Applications"
            style={{
              position: 'fixed',
              top: anchor.top,
              right: anchor.right,
              minWidth: 220,
              background: 'var(--card-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-card)',
              padding: '8px 0',
              zIndex: 2000,
            }}
          >
            <div
              style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                color: 'var(--text-muted)',
                padding: '6px 14px 4px',
              }}
            >
              Applications
            </div>
            {items.map((app) => {
              const Icon = APP_ICONS[app.key] ?? LayoutGrid;
              if (app.isCurrent) {
                return (
                  <div
                    key={app.key}
                    role="menuitem"
                    aria-current="true"
                    aria-disabled="true"
                    style={{ ...itemStyle, color: 'var(--accent)', fontWeight: 600 }}
                  >
                    <Icon size={14} />
                    {app.name}
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '1px 8px',
                        borderRadius: 999,
                        background: 'var(--glass)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      actuelle
                    </span>
                  </div>
                );
              }
              return (
                <a
                  key={app.key}
                  role="menuitem"
                  href={app.url}
                  target={windowNameFor(app.key)}
                  title={`Ouvrir ${app.name} dans son onglet`}
                  onClick={(e) => {
                    setOpen(false);
                    // Clic milieu / Cmd+clic : laissés au navigateur.
                    if (!isPlainLeftClick(e)) return;
                    e.preventDefault();
                    openEcosystemApp(app, { win: window, opened: openedTabs });
                  }}
                  style={{ ...itemStyle, color: 'var(--text)', cursor: 'pointer' }}
                >
                  <Icon size={14} />
                  {app.name}
                </a>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
